import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { components } from '@doltech/pageManagement/dto';
import { InternalPageLink } from '@doltech/routeDefinition';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box as BoxV2 } from '@doltech/ui/lib/figma/Common/BoxV2';
import { ContentItemProps } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TableOfContentType';
import { BlogCoverProps } from '@doltech/ui/lib/figma/Blog/BlogCover';
import { SpaceProps } from 'antd';
import * as React from 'react';
import styled from 'styled-components';
import { BlogHeaderProps } from './ReadingBlogHeader';
import { BlogTableOfContent } from './GrammarTableOfContent';
import { FloatingButton } from './FloatingButton';
import { InteractionBottomGrammarNavigation } from './InteractionBottomGrammarNavigation';
import { verticalSpace } from '@doltech/utils/lib/css-style';
import { ThumbnailBlogGrammar } from './ThumbnailBlogGrammar';
import { ScrollToTopButton } from '../core-blog-editor/ScrollToTopButton';
import { useOnScreen } from '@doltech/ui/lib/hooks/useOnScreen';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { TitleComponentBlogGrammar } from './TitleComponentBlogGrammar';

const TitleBlogGrammar = dynamic(() => import('./TitleComponentBlogGrammar'), {
  loading: () => <TitleComponentBlogGrammar isLoading />,
});

const HEADER_HEIGHT = 64;
const Main = styled(BoxV2).withConfig({
  componentId: 'BlogLayout_Main',
} as any)`
  background-color: ${colorsV2.white100};
  ${verticalSpace(26)};

  ${fromScreen(768)} {
    ${verticalSpace(26)}
  }

  table {
    width: 100%;
  }

  #landing-layout {
    background-color: ${colorsV2.white100};
  }

  b {
    font-weight: 700;
  }

  strong {
    font-weight: 700;
  }

  .block-spacing {
    margin-bottom: 32px;
  }

  .border-and-shading-box {
    padding: 8px 12px;

    ${fromScreen(1366)} {
      padding: 24px 32px;
    }
  }

  .header-editor {
    font-family: Inter, sans-serif;
    margin-bottom: 16px;
  }
`;

const BlogContent = styled.div`
  a {
    text-decoration: underline;
  }

  h2 {
    font-size: 24px;
    line-height: 32px;
    color: ${colorsV2.gray180};
    font-weight: 600;
  }

  h3 {
    font-size: 20px;
    line-height: 28px;
    color: ${colorsV2.gray180};
    font-weight: 600;
  }

  h4 {
    font-size: 16px;
    line-height: 24px;
    color: ${colorsV2.gray180};
    font-weight: 600;
  }

  p {
    font-size: 16px;
    line-height: 28px;
    font-weight: 400;
  }

  ${fromScreen(768)} {
    h2 {
      font-size: 28px;
      line-height: 36px;
      color: ${colorsV2.gray180};
      font-weight: 600;
    }

    h3 {
      font-size: 22px;
      line-height: 28px;
      color: ${colorsV2.gray180};
      font-weight: 600;
    }
  }
`;

const ResponsiveBlogSection = styled.div`
  .top-author {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .user-info {
    padding: 24px 0 20px;
  }

  .horizontal-line {
    display: none;
  }

  ${fromScreen(768)} {
    .top-author {
      padding-top: 28px;
      padding-bottom: 28px;
    }

    .user-info {
      padding: 0 0 24px;
    }
  }

  ${fromScreen(1366)} {
    .top-author {
      padding-bottom: 0;
    }

    .user-info {
      display: none;
    }

    .horizontal-line {
      display: flex;
    }
  }
`;

export interface BlogLayoutProps extends SpaceProps {
  header?: Omit<BlogHeaderProps, 'socials' | 'headerEditor'>;
  cover?: BlogCoverProps;
  coverEditor?: BlogCoverProps;
  socials: any;
  children?: any;

  tableOfContent?: ContentItemProps[];
  tableOfContentEditor?: any[];
  title?: string;

  seoData?: components['schemas']['PageResponseDTOObject'];
  internalPageLink?: InternalPageLink;
  useSideBar?: boolean;
  useFooter?: boolean;
  headerEditor?: React.ReactNode;
  customSticky?: React.ReactNode;
  detailInfo?: React.ReactNode;
  renderTopAuthor?: React.ReactNode;
  renderBottomAuthor?: React.ReactNode;
  renderUserInfo?: React.ReactNode;
  initHeader?: any;
}

export const GrammarLandingBlogEditorLayout = observer((props: BlogLayoutProps) => {
  const {
    initHeader,
    children,
    tableOfContent = [],
    tableOfContentEditor = [],
    title = undefined,
    seoData,
    detailInfo,
    renderTopAuthor,
    renderBottomAuthor,
    renderUserInfo,
  } = props;
  const { appScrollRef } = useAppScrollRef();
  const scrollToTopRef = React.useRef<HTMLDivElement>(null);
  const { isOnScreen } = useOnScreen(scrollToTopRef, `-${HEADER_HEIGHT}px`);
  return (
    <Main id="blog-layout">
      <ResponsiveBlogSection ref={scrollToTopRef} className="responsive-blog-section">
        <div className="user-info">{renderUserInfo}</div>
        <TitleBlogGrammar
          blogTitle={initHeader?.blogTitle}
          blogDescription={initHeader?.blogDescription}
        />
        <div className="top-author">{renderTopAuthor}</div>
        <div id="header-divider" className="horizontal-line" />
        <ThumbnailBlogGrammar
          image={initHeader?.image}
          alt={initHeader?.alt}
          caption={initHeader?.caption}
          imgTitle={initHeader?.imgTitle}
        />
        <BlogTableOfContent
          stickyMode="ALWAYS_SHOW"
          items={tableOfContent}
          editorItems={tableOfContentEditor}
          tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title || title}
          stickyId="blog-table-of-content"
          scrollContainerElm={appScrollRef.current}
          stickOnElm="#table-of-content-stick"
          hideWhenIntersectWithElms={[
            '.recommend-blog-list',
            '.section-free-test-promotion',
            '#shared-footer',
          ]}
          offsetY={100}
          stickOnElmPosition={{
            align: 'left',
            gapInPx: 36,
          }}
        />
        <div id="table-of-content-stick" />
      </ResponsiveBlogSection>

      <BlogContent>{children}</BlogContent>
      {renderBottomAuthor && (
        <>
          <div id="header-divider" className="horizontal-line" />
          {renderBottomAuthor}
        </>
      )}
      <InteractionBottomGrammarNavigation
        layout="responsive"
        scrollContainerElm={appScrollRef.current}
        tocTitle={title}
        tocItems={tableOfContent}
        tocEditorItems={tableOfContentEditor}
        detailInfo={detailInfo}
      />
      <ScrollToTopButton visible={scrollToTopRef.current && !isOnScreen} />

      <FloatingButton
        layout="responsive"
        scrollContainerElm={appScrollRef.current}
        tocTitle={title}
        tocItems={tableOfContent}
        tocEditorItems={tableOfContentEditor}
        detailInfo={detailInfo}
      />
    </Main>
  );
});
